import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { archiveService, ArchivePost, ArchiveStats, ArchiveFeedParams, CreateArchiveData, UpdateArchiveData } from '../../services/archive.service';
import { requestCache } from '../../utils/requestCache';
import { friendlyErrorMessage } from '../../lib/errorMessages';
import type { ApiErrorResponse } from '../../services/apiClient';

// Types
export interface ArchivePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ArchiveState {
  // Feed data (public or filtered) - accumulated from all loaded pages
  feedItems: ArchivePost[];
  feedPagination: ArchivePagination | null;
  feedLoading: boolean;
  feedError: string | null;
  
  // Pagination & caching state
  currentPage: number;
  loadedPages: number[];
  cacheKey: string | null; // Track current filter/sort combination
  
  // My archive data (authenticated user)
  myItems: ArchivePost[];
  myPagination: ArchivePagination | null;
  myLoading: boolean;
  myError: string | null;
  
  // Stats
  stats: ArchiveStats | null;
  statsLoading: boolean;
  statsError: string | null;
  
  // Single post
  currentPost: ArchivePost | null;
  currentPostLoading: boolean;
  currentPostError: string | null;

  // Cross-page post cache, keyed by post id. Populated by every list/detail
  // fetch so any later view (single post, favorites, profile re-mount) can
  // render instantly without an extra round-trip. Stale-while-revalidate is
  // fine here — favorites + post detail views mostly need the post to *exist*.
  postsById: Record<string, ArchivePost>;
  
  // General loading states
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  
  // UI state
  showFilterDrawer: boolean;
  
  lastFetched: number | null;
}

// Initial state
const initialState: ArchiveState = {
  feedItems: [],
  feedPagination: null,
  feedLoading: false,
  feedError: null,
  
  // Pagination & caching state
  currentPage: 0,
  loadedPages: [],
  cacheKey: null,
  
  myItems: [],
  myPagination: null,
  myLoading: false,
  myError: null,
  
  stats: null,
  statsLoading: false,
  statsError: null,
  
  currentPost: null,
  currentPostLoading: false,
  currentPostError: null,
  
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  
  showFilterDrawer: false,

  lastFetched: null,

  postsById: {},
};

// Helper function to generate cache key from params
const generateCacheKey = (params: ArchiveFeedParams): string => {
  const { sortBy = 'createdAt', order = 'desc', userId, status } = params;
  // Only include status in cache key if explicitly provided
  // This ensures different cache keys for "all posts" vs "published only"
  const cacheParams: any = { sortBy, order, userId };
  if (status !== undefined) {
    cacheParams.status = status;
  }
  return JSON.stringify(cacheParams);
};

// Async thunks
/**
 * Fetch archive feed with intelligent caching and pagination
 * - Data is shared across Home, ArchiveFeed, and all pages using feedItems
 * - Smart caching: checks if data exists and is fresh before fetching
 * - Accumulates pages for infinite scroll
 * - Resets data when filters change
 * - Single source of truth for all archive posts
 */
const FEED_TTL_MS = 60_000;

export const fetchArchiveFeed = createAsyncThunk(
  'archive/fetchFeed',
  async (params: ArchiveFeedParams = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const archive = state.archive;
      const currentCacheKey = archive?.cacheKey;

      const newCacheKey = generateCacheKey(params);
      const requestedPage = params.page || 1;

      const filtersChanged = currentCacheKey !== newCacheKey;

      // TTL cache: when the same filters + page were just loaded and the data is
      // still fresh, resolve from Redux state instead of hitting the API. We
      // always resolve as fulfilled so callers awaiting the dispatch result
      // (e.g. SocialProfile copying items into local state) keep working.
      const sameFilters = !filtersChanged;
      const pageLoaded = (archive?.loadedPages || []).includes(requestedPage);
      const fresh = archive?.lastFetched && (Date.now() - archive.lastFetched) < FEED_TTL_MS;
      const hasItems = (archive?.feedItems || []).length > 0;

      if (sameFilters && pageLoaded && fresh && hasItems) {
        return {
          items: archive.feedItems,
          pagination: archive.feedPagination,
          cacheKey: newCacheKey,
          page: requestedPage,
          filtersChanged: false,
          isFromCache: true,
        };
      }

      const { feedService } = await import('../../features/feed');
      // Coalesce concurrent identical feed loads (same filters + page + limit)
      // into a single request. The TTL check above only dedups *sequential*
      // calls after data has landed; this handles the *overlapping* case —
      // StrictMode's double dispatch, or Home/Explore + SocialProfile asking
      // for the same page on the same tick — so a slow-network user downloads
      // the feed once, not twice.
      const dedupKey = `feed:${newCacheKey}:p${requestedPage}:l${params.limit || 20}`;
      const response = await requestCache.inflight(dedupKey, () =>
        feedService.getSmartFeed(params.page || 1, params.limit || 20, {
          filters: params,
        })
      );

      if (response.success && response.data) {
        return {
          ...response.data,
          cacheKey: newCacheKey,
          page: requestedPage,
          filtersChanged,
          isFromCache: false
        };
      }
      return rejectWithValue('Failed to fetch archive feed');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch archive feed');
    }
  }
);

export const fetchMyArchive = createAsyncThunk(
  'archive/fetchMine',
  async (params: Omit<ArchiveFeedParams, 'userId'> = {}, { rejectWithValue }) => {
    try {
      const cacheKey = `fetchMyArchive_${JSON.stringify(params)}`;
      const response = await requestCache.get(cacheKey, () =>
        archiveService.getMine(params)
      );
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue('Failed to fetch my archive');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch my archive');
    }
  }
);

export const fetchArchiveStats = createAsyncThunk(
  'archive/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await archiveService.getMyStats();
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue('Failed to fetch archive stats');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch archive stats');
    }
  }
);

export const fetchArchivePost = createAsyncThunk(
  'archive/fetchPost',
  async (id: string, { rejectWithValue }) => {
    try {
      // Always fetch fresh (post carries live user info), but coalesce
      // concurrent duplicate loads — StrictMode's double-invoked effect, or a
      // list→detail navigation that mounts the page twice — into ONE network
      // call instead of two.
      const response = await requestCache.inflight(`archivePost_${id}`, () =>
        archiveService.getById(id)
      );

      // console.log('archivesinlgepost', response)
      if (response.success && response.data) {
        return response.data.post;
      }
      return rejectWithValue('Failed to fetch archive post');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch archive post');
    }
  }
);

/** What the UI shows when a post can't be created and we can't say why. */
const CREATE_POST_FALLBACK = "We couldn't create your post. Please try again.";

/**
 * Rejection payload for create. Carries the message the user should read plus
 * the machine code, so call sites can branch (e.g. keep the form open and let
 * them retry) without re-deriving it from a string.
 */
export interface ArchivePostError {
  message: string;
  code?: string | number;
}

export const createArchivePost = createAsyncThunk(
  'archive/create',
  async (
    data: {
      images: File[];
      metadata?: CreateArchiveData;
      /** Aborts the in-flight upload — wired to the modal's Cancel button. */
      signal?: AbortSignal;
      /** Byte-level progress for the upload bar. */
      onUploadProgress?: (loaded: number, total: number) => void;
    },
    { rejectWithValue },
  ) => {
    try {
      const { images, metadata, signal, onUploadProgress } = data;
      const response = await archiveService.createPost(images, metadata, {
        signal,
        onUploadProgress: onUploadProgress
          ? (event) => onUploadProgress(event.loaded, event.total ?? 0)
          : undefined,
      });
      if (response.success && response.data) {
        return response.data.post;
      }
      // apiClient resolves failures instead of throwing, so the real reason
      // arrives here — translate it rather than discarding it.
      return rejectWithValue({
        message: friendlyErrorMessage(response, CREATE_POST_FALLBACK),
        code: (response as ApiErrorResponse).error?.appCode ?? (response as ApiErrorResponse).error?.code,
      } satisfies ArchivePostError);
    } catch (error: any) {
      return rejectWithValue({
        message: friendlyErrorMessage(error, CREATE_POST_FALLBACK),
        code: error?.code,
      } satisfies ArchivePostError);
    }
  }
);

export const updateArchivePost = createAsyncThunk(
  'archive/update',
  async (data: { id: string; updates: UpdateArchiveData }, { rejectWithValue }) => {
    try {
      const { id, updates } = data;
      const response = await archiveService.update(id, updates);
      if (response.success && response.data) {
        return response.data.post;
      }
      return rejectWithValue('Failed to update archive post');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update archive post');
    }
  }
);

export const deleteArchivePost = createAsyncThunk(
  'archive/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await archiveService.remove(id);
      if (response.success) {
        return id;
      }
      return rejectWithValue('Failed to delete archive post');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete archive post');
    }
  }
);

export const likeArchivePost = createAsyncThunk(
  'archive/like',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await archiveService.like(id);
      if (response.success) {
        return id;
      }
      return rejectWithValue('Failed to like archive post');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to like archive post');
    }
  }
);

// Slice
const archiveSlice = createSlice({
  name: 'archive',
  initialState,
  reducers: {
    clearArchiveFeed: (state) => {
      state.feedItems = [];
      state.feedPagination = null;
      state.feedError = null;
      state.currentPage = 0;
      state.loadedPages = [];
      state.cacheKey = null;
      state.lastFetched = null;
    },
    clearMyArchive: (state) => {
      state.myItems = [];
      state.myPagination = null;
      state.myError = null;
    },
    clearCurrentPost: (state) => {
      state.currentPost = null;
      state.currentPostError = null;
    },
    // Seed the detail view synchronously with the post object the feed card
    // already holds, so opening a post paints instantly instead of flashing a
    // skeleton while fetchArchivePost runs in the background.
    setCurrentPost: (state, action: PayloadAction<ArchivePost>) => {
      state.currentPost = action.payload;
      state.currentPostLoading = false;
      state.currentPostError = null;
    },
    addArchivePost: (state, action: PayloadAction<ArchivePost>) => {
      // Add to my items if it exists
      if (state.myItems.length > 0) {
        state.myItems.unshift(action.payload);
      }
      // Always seed the by-id cache so any later view picks it up.
      state.postsById[action.payload.id] = action.payload;
    },
    /**
     * Drop a post (or several) into the cross-page cache. Use this whenever
     * a service call returns a post outside the feed flow (e.g. detail page,
     * favorites hydration, profile fetch). Existing entries are overwritten
     * so the freshest copy wins.
     */
    cacheArchivePosts: (state, action: PayloadAction<ArchivePost | ArchivePost[]>) => {
      const list = Array.isArray(action.payload) ? action.payload : [action.payload];
      list.forEach((p) => {
        if (p && p.id) state.postsById[p.id] = p;
      });
    },
    removeArchivePost: (state, action: PayloadAction<string>) => {
      state.feedItems = state.feedItems.filter(item => item.id !== action.payload);
      state.myItems = state.myItems.filter(item => item.id !== action.payload);
      delete state.postsById[action.payload];
    },
    updateArchivePostInState: (state, action: PayloadAction<ArchivePost>) => {
      const updatePost = (items: ArchivePost[]) => {
        const index = items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          items[index] = action.payload;
        }
      };
      updatePost(state.feedItems);
      updatePost(state.myItems);

      if (state.currentPost?.id === action.payload.id) {
        state.currentPost = action.payload;
      }
      state.postsById[action.payload.id] = action.payload;
    },
    incrementLikeCount: (state, action: PayloadAction<string>) => {
      const incrementLike = (items: ArchivePost[]) => {
        const post = items.find(item => item.id === action.payload);
        if (post) {
          post.likeCount += 1;
        }
      };
      incrementLike(state.feedItems);
      incrementLike(state.myItems);
      
      if (state.currentPost?.id === action.payload) {
        state.currentPost.likeCount += 1;
      }
    },
    toggleFilterDrawer: (state) => {
      state.showFilterDrawer = !state.showFilterDrawer;
    },
    setFilterDrawer: (state, action: PayloadAction<boolean>) => {
      state.showFilterDrawer = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch feed
      .addCase(fetchArchiveFeed.pending, (state) => {
        state.feedLoading = true;
        state.feedError = null;
      })
      .addCase(fetchArchiveFeed.fulfilled, (state, action) => {
        state.feedLoading = false;
        
        const { items = [], pagination, cacheKey, page, filtersChanged = false, isFromCache = false } = action.payload;
        
        // If filters changed, reset everything
        if (filtersChanged) {
          state.feedItems = items;
          state.loadedPages = [page];
          state.currentPage = page;
        } else if (!isFromCache) {
          // Same filters, append new page data
          if (!state.loadedPages.includes(page)) {
            // Only add items that don't already exist (prevent duplicates)
            const newItems = items.filter(
              (newItem: ArchivePost) => !state.feedItems.some(existing => existing.id === newItem.id)
            );
            state.feedItems.push(...newItems);
            state.loadedPages.push(page);
          }
          state.currentPage = page;
        }
        // If isFromCache=true, we don't modify feedItems (already have the data)

        state.feedPagination = pagination || null;
        state.cacheKey = cacheKey;
        state.lastFetched = Date.now();

        // Seed cross-page cache so detail / favorites views skip a roundtrip.
        items.forEach((p: ArchivePost) => {
          if (p && p.id) state.postsById[p.id] = p;
        });
      })
      .addCase(fetchArchiveFeed.rejected, (state, action) => {
        state.feedLoading = false;
        state.feedError = action.payload as string;
      })
      
      // Fetch my archive
      .addCase(fetchMyArchive.pending, (state) => {
        state.myLoading = true;
        state.myError = null;
      })
      .addCase(fetchMyArchive.fulfilled, (state, action) => {
        state.myLoading = false;
        state.myItems = action.payload.items || [];
        state.myPagination = action.payload.pagination || null;
        state.lastFetched = Date.now();
        (action.payload.items || []).forEach((p: ArchivePost) => {
          if (p && p.id) state.postsById[p.id] = p;
        });
      })
      .addCase(fetchMyArchive.rejected, (state, action) => {
        state.myLoading = false;
        state.myError = action.payload as string;
      })
      
      // Fetch stats
      .addCase(fetchArchiveStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchArchiveStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchArchiveStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload as string;
      })
      
      // Fetch single post
      .addCase(fetchArchivePost.pending, (state) => {
        state.currentPostLoading = true;
        state.currentPostError = null;
      })
      .addCase(fetchArchivePost.fulfilled, (state, action) => {
        state.currentPostLoading = false;
        state.currentPost = action.payload;
        if (action.payload?.id) {
          state.postsById[action.payload.id] = action.payload;
        }
      })
      .addCase(fetchArchivePost.rejected, (state, action) => {
        state.currentPostLoading = false;
        state.currentPostError = action.payload as string;
      })
      
      // Create post
      .addCase(createArchivePost.pending, (state) => {
        state.isCreating = true;
      })
      .addCase(createArchivePost.fulfilled, (state, action) => {
        state.isCreating = false;
        // Add new post to the beginning of my items
        state.myItems.unshift(action.payload);
        
        // Invalidate feed cache since new content is available
        state.lastFetched = null;
        requestCache.clear();
      })
      .addCase(createArchivePost.rejected, (state) => {
        state.isCreating = false;
      })
      
      // Update post
      .addCase(updateArchivePost.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateArchivePost.fulfilled, (state, action) => {
        state.isUpdating = false;
        // Update post in all relevant arrays
        const updatePost = (items: ArchivePost[]) => {
          const index = items.findIndex(item => item.id === action.payload.id);
          if (index !== -1) {
            items[index] = action.payload;
          }
        };
        updatePost(state.feedItems);
        updatePost(state.myItems);
        
        if (state.currentPost?.id === action.payload.id) {
          state.currentPost = action.payload;
        }
      })
      .addCase(updateArchivePost.rejected, (state) => {
        state.isUpdating = false;
      })
      
      // Delete post
      .addCase(deleteArchivePost.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteArchivePost.fulfilled, (state, action) => {
        state.isDeleting = false;
        // Remove post from all arrays
        state.feedItems = state.feedItems.filter(item => item.id !== action.payload);
        state.myItems = state.myItems.filter(item => item.id !== action.payload);
        
        if (state.currentPost?.id === action.payload) {
          state.currentPost = null;
        }
        
        // Invalidate feed cache since content was removed
        state.lastFetched = null;
        requestCache.clear();
      })
      .addCase(deleteArchivePost.rejected, (state) => {
        state.isDeleting = false;
      })
      
      // Like post
      .addCase(likeArchivePost.fulfilled, (state, action) => {
        // Increment like count for the post
        const incrementLike = (items: ArchivePost[]) => {
          const post = items.find(item => item.id === action.payload);
          if (post) {
            post.likeCount += 1;
          }
        };
        incrementLike(state.feedItems);
        incrementLike(state.myItems);
        
        if (state.currentPost?.id === action.payload) {
          state.currentPost.likeCount += 1;
        }
      });
  },
});

export const {
  clearArchiveFeed,
  clearMyArchive,
  clearCurrentPost,
  setCurrentPost,
  addArchivePost,
  cacheArchivePosts,
  removeArchivePost,
  updateArchivePostInState,
  incrementLikeCount,
  toggleFilterDrawer,
  setFilterDrawer,
} = archiveSlice.actions;

// Memoized selectors for easier access to pagination state
const selectArchiveState = (state: { archive: ArchiveState }) => state.archive;

export const selectFeedPagination = createSelector(
  [selectArchiveState],
  (archive) => ({
    currentPage: archive.currentPage,
    totalPages: archive.feedPagination?.totalPages || 0,
    hasMore: archive.currentPage < (archive.feedPagination?.totalPages || 0),
    loadedPages: archive.loadedPages,
  })
);

export const selectPostsById = (state: { archive: ArchiveState }) =>
  state.archive.postsById;

export default archiveSlice.reducer;
